import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { QueryKey } from '@/Config/api/queryKey';
import {
  CreditTopupAutoReload as CreditTopupAutoReloadDto,
  CreditTopupAutoReloadInput,
  CreditTopupCardSetupInput,
  CreditTopupCheckoutUrl,
  CreditTopupEntrypoint,
  CreditTopupOffer,
} from '@/Domain/api/Subscription/dto/CreditTopup.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Checkbox } from '@/Domain/components/Shared/CheckBox/Checkbox';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { saveCreditTopupCheckoutAttribution } from '@/Domain/components/Subscription/CreditTopup/creditTopupAnalytics';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

type Props = {
  offer: CreditTopupOffer;
  offerCardSetup: boolean;
  entrypoint: CreditTopupEntrypoint;
  canEnable?: boolean;
};

const CHECKBOX_ID = 'credit-topup-auto-reload';

export const CreditTopupAutoReload = ({
  offer,
  offerCardSetup,
  entrypoint,
  canEnable = true,
}: Props) => {
  const { t } = useTranslation('subscription_page');
  const { capture } = usePostHogAnalytics();
  const { auto_reload: autoReload } = offer;

  const { mutate: setAutoReload, isPending } = useMutation<
    CreditTopupAutoReloadDto,
    unknown,
    CreditTopupAutoReloadInput
  >({
    mutationKey: [QueryKey.subscription.creditTopupAutoReload],
    onSuccess: (_data, { enabled }) => {
      capture(PostHogEventEnum.CreditTopupAutoReloadToggled, { enabled });
      toast.success(
        t(enabled ? 'topup_panel.auto_reload.turned_on' : 'topup_panel.auto_reload.turned_off'),
      );
    },
    onError: () => toast.error(t('topup_panel.auto_reload.error')),
  });

  const { mutate: startCardSetup, isPending: isSavingCard } = useMutation<
    CreditTopupCheckoutUrl,
    unknown,
    CreditTopupCardSetupInput
  >({
    mutationKey: [QueryKey.subscription.creditTopupCardSetup],
    onSuccess: ({ url }, input) => {
      saveCreditTopupCheckoutAttribution({
        flow: 'card_setup',
        entrypoint: input.entrypoint,
        auto_reload: true,
        has_balance: offer.topup_credits > 0,
      });
      capture(PostHogEventEnum.CreditTopupCardSetupStarted, {
        entrypoint: input.entrypoint,
        has_balance: offer.topup_credits > 0,
        replacing_card: autoReload.available,
      });
      window.location.href = url;
    },
    onError: (_error, input) => {
      capture(PostHogEventEnum.CreditTopupCardSetupCreationFailed, {
        entrypoint: input.entrypoint,
        has_balance: offer.topup_credits > 0,
        replacing_card: autoReload.available,
      });
      toast.error(t('topup_panel.auto_reload.error'));
    },
  });

  const credits = offer.credits.toLocaleString();

  const onCheckedChange = (value: boolean | 'indeterminate') => {
    const enabled = value === true;
    if (enabled && !canEnable) return;
    if (enabled && !autoReload.available) return;
    setAutoReload({ enabled });
  };

  if (offerCardSetup && !autoReload.available) {
    return (
      <div className="flex flex-col gap-2">
        <Button
          variant="secondary"
          className="w-full"
          loading={isSavingCard}
          onClick={() => startCardSetup({ entrypoint })}
          data-testid="credit-topup-save-card-button"
        >
          {t('topup_panel.auto_reload.save_card_cta')}
        </Button>
        <p className="text-xs text-foreground-subtle">
          {t('topup_panel.auto_reload.save_card_hint')}
        </p>
      </div>
    );
  }

  if (!autoReload.available && !autoReload.enabled) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-2.5">
        <Checkbox
          id={CHECKBOX_ID}
          checked={autoReload.enabled}
          disabled={isPending || (!canEnable && !autoReload.enabled)}
          onCheckedChange={onCheckedChange}
          className="mt-0.5"
          data-testid="credit-topup-auto-reload-checkbox"
        />
        <div className="min-w-0">
          <Label htmlFor={CHECKBOX_ID} className="cursor-pointer font-normal leading-snug">
            {t(
              !canEnable
                ? 'topup_panel.auto_reload.unavailable_label'
                : 'topup_panel.auto_reload.label',
              { credits, price: offer.price_usd },
            )}
          </Label>
        </div>
      </div>
      {canEnable && autoReload.available && (
        <div className="flex min-h-16 flex-col items-start gap-1" aria-live="polite">
          {autoReload.enabled && (
            <p
              className="animate-in fade-in slide-in-from-top-1 text-xs text-muted-foreground duration-200"
              data-testid="credit-topup-auto-reload-status"
            >
              {t(
                autoReload.card_last4
                  ? 'topup_panel.auto_reload.on_status_card'
                  : 'topup_panel.auto_reload.on_status',
                {
                  threshold: autoReload.threshold_credits.toLocaleString(),
                  card: autoReload.card_brand,
                  last4: autoReload.card_last4,
                },
              )}
            </p>
          )}
          <Button
            variant="link"
            size="xs"
            className="shrink-0"
            loading={isSavingCard}
            onClick={() => startCardSetup({ entrypoint })}
            data-testid="credit-topup-change-card-button"
          >
            {t(
              autoReload.enabled
                ? 'topup_panel.auto_reload.change_card'
                : 'topup_panel.auto_reload.change_card_and_turn_on',
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
